import accountantApi from "api/accountant";
import {USCitiesByZipCode} from "assets/jsons";
import Modal from "components/Modal";
import ModalLayout from "components/ModalLayout";
import Selector from "components/Selector";
import Tooltip from "components/Tooltip";
import useAccountant from "contexts/User/useAccountant";
import {handleError} from "helpers/errors";
import {AwaitableModal} from "hooks/useAwaitableModal";
import SoftwareExperience from "pages/accountant/Setup/components/SoftwareExperience";
import {useState} from "react";

interface Props {
  name: string;
  bio: string;
  zip: string;
  employmentType: string;
  softwareExperience: Record<string, boolean>;
  linkedIn?: string;
}

const EditProfile: AwaitableModal<Props & {location: string}, Props> = ({initialData, open, onCancel, resolve}) => {
  const [name, setName] = useState(initialData.name);
  const [zip, setZip] = useState(initialData.zip);
  const {city, state} = USCitiesByZipCode[zip as keyof typeof USCitiesByZipCode] || {};
  const [bio, setBio] = useState(initialData.bio);
  const [linkedIn] = useState(initialData.linkedIn);
  const [employmentType, setEmploymentType] = useState<string>(initialData.employmentType);
  const [softwareExperience, setSoftwareExperience] = useState<string[]>(
    Object.keys(initialData.softwareExperience || {}),
  );
  const [submitting, setSubmitting] = useState(false);
  const canHaveEmployment = !useAccountant().enterprise;

  const onEdit = () => {
    if (submitting) return;
    if (!name) return handleError("Please enter your name");
    if (!bio) return handleError("Please enter your biography");
    if (!zip) return handleError("Please enter your zip code");
    if (!city || !state) return handleError("Zip Code Not Found");
    if (!employmentType) return handleError("Please select your employment type");
    if (linkedIn && !linkedIn.includes("linkedin.com")) return handleError("Please enter a valid LinkedIn Profile URL");

    const softwareExperienceTransformed = softwareExperience.reduce((acc, item) => ({...acc, [item]: true}), {});

    setSubmitting(true);
    accountantApi
      .updateProfile({
        name,
        bio,
        state,
        city,
        zip,
        employmentType,
        softwareExperience: softwareExperienceTransformed,
        linkedIn,
      })
      .then(() => {
        resolve({
          name,
          bio,
          zip,
          location: `${city.toLowerCase().trim()}, ${state.toLowerCase().trim()}`,
          employmentType,
          softwareExperience: softwareExperienceTransformed,
          linkedIn,
        });
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <Modal open={open} close={!submitting ? onCancel : () => {}}>
      <ModalLayout
        title="Edit Profile"
        scrollable
        footer={
          <>
            <button className="btn-secondary" disabled={submitting} onClick={!submitting ? onCancel : () => {}}>
              Cancel
            </button>
            <button className="btn-primary" disabled={submitting} onClick={onEdit}>
              Save Changes
            </button>
          </>
        }
      >
        <input type="text" placeholder="Name" value={name} onChange={(e) => setName(e.currentTarget.value)} />
        <textarea
          className="w-full h-[11rem] min-h-[11rem] resize-none"
          rows={6}
          placeholder="Biography"
          value={bio}
          onChange={(e) => setBio(e.currentTarget.value)}
        />
        {canHaveEmployment && (
          <Tooltip
            position="top"
            text={
              'Indicate whether you are looking for full time, part time, or contract work. Choose "Flexible" to indicate any of the above.'
            }
            className="z-[100000000]"
          >
            <Selector value={employmentType || "Employment Type"}>
              {["Full Time", "Part Time", "Contract", "Flexible"].map((t) => (
                <div
                  key={t}
                  onClick={() => setEmploymentType(t)}
                  className="px-4 py-3 text-sm text-brand-navy cursor-pointer select-none hover:bg-brand-cream"
                >
                  {t}
                </div>
              ))}
            </Selector>
          </Tooltip>
        )}
        <div className="relative">
          <input
            type="text"
            className="w-full"
            placeholder="Zip Code"
            value={zip}
            onChange={(e) => setZip(e.currentTarget.value.trim().replaceAll(" ", ""))}
          />
          {!!city && (
            <div className="absolute right-4 top-0 h-full flex items-center pointer-events-none text-brand-muted text-sm">
              {city}, {state}
            </div>
          )}
        </div>
        <SoftwareExperience experience={softwareExperience} setExperience={setSoftwareExperience} top />
      </ModalLayout>
    </Modal>
  );
};

export default EditProfile;
